import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Ticket, Plus, Clock, CheckCircle, Warning, PaperPlaneRight, Paperclip, Lock, ArrowLeft, User, GearSix, X, PencilSimple, Trash, ArrowsDownUp } from "@phosphor-icons/react";
import UserAvatar from "@/components/UserAvatar";
import { clientDisplayName } from "@/lib/clientDisplay";

const PRIORITY_STYLES = {
  // Session 68 — Bug 8: Each entry includes a `cls` shortcut that
  // pins both `bg` and `hover:bg` to the same colour. Without this
  // the shadcn Badge default variant (`hover:bg-primary/80`) leaks
  // through and dims the badge on hover, making static labels look
  // clickable. `cursor-default` is added so the cursor doesn't flip
  // to a pointer either. Every call site that renders a static
  // priority/status badge should use this `cls` instead of
  // composing `bg`/`text` manually.
  critical: { bg: "bg-[#FEF2F2]", text: "text-[#DC2626]", label: "Critical", cls: "bg-[#FEF2F2] hover:bg-[#FEF2F2] text-[#DC2626] cursor-default" },
  high:     { bg: "bg-[#FFFBEB]", text: "text-[#D97706]", label: "High",     cls: "bg-[#FFFBEB] hover:bg-[#FFFBEB] text-[#D97706] cursor-default" },
  medium:   { bg: "bg-[#F0F7FF]", text: "text-[#0055FF]", label: "Medium",   cls: "bg-[#F0F7FF] hover:bg-[#F0F7FF] text-[#0055FF] cursor-default" },
  low:      { bg: "bg-[#F5F5F5]", text: "text-[#525252]", label: "Low",      cls: "bg-[#F5F5F5] hover:bg-[#F5F5F5] text-[#525252] cursor-default" },
};

const STATUS_STYLES = {
  open:        { bg: "bg-[#FFFBEB]", text: "text-[#D97706]", label: "Open",        cls: "bg-[#FFFBEB] hover:bg-[#FFFBEB] text-[#D97706] cursor-default" },
  in_progress: { bg: "bg-[#F0F7FF]", text: "text-[#0055FF]", label: "In Progress", cls: "bg-[#F0F7FF] hover:bg-[#F0F7FF] text-[#0055FF] cursor-default" },
  responded:   { bg: "bg-[#EFF6FF]", text: "text-[#2563EB]", label: "Responded",   cls: "bg-[#EFF6FF] hover:bg-[#EFF6FF] text-[#2563EB] cursor-default" },
  resolved:    { bg: "bg-[#F0FDF4]", text: "text-[#16A34A]", label: "Resolved",    cls: "bg-[#F0FDF4] hover:bg-[#F0FDF4] text-[#16A34A] cursor-default" },
  closed:      { bg: "bg-[#F5F5F5]", text: "text-[#525252]", label: "Closed",      cls: "bg-[#F5F5F5] hover:bg-[#F5F5F5] text-[#525252] cursor-default" },
  cancelled:   { bg: "bg-[#FEF2F2]", text: "text-[#DC2626]", label: "Cancelled",   cls: "bg-[#FEF2F2] hover:bg-[#FEF2F2] text-[#DC2626] cursor-default" },
};

// Session 68 pt4 — pastel palette for ticket categories. Each entry
// pins both `bg` and `hover:bg` (matching the Bug-8 fix on status
// badges) so the chip never dims on hover. The palette is hand-
// picked to be distinguishable at a glance even when several rows
// stack — adjacent hues live in different families.
const CATEGORY_STYLES = {
  general:         { label: "General",         cls: "bg-[#F5F5F5] hover:bg-[#F5F5F5] text-[#525252] cursor-default" },
  technical:       { label: "Technical",       cls: "bg-[#FCE7F3] hover:bg-[#FCE7F3] text-[#9D174D] cursor-default" },
  access:          { label: "Access",          cls: "bg-[#FEF3C7] hover:bg-[#FEF3C7] text-[#92400E] cursor-default" },
  billing:         { label: "Billing",         cls: "bg-[#FFE4E6] hover:bg-[#FFE4E6] text-[#9F1239] cursor-default" },
  feature_request: { label: "Feature",         cls: "bg-[#DBEAFE] hover:bg-[#DBEAFE] text-[#1E40AF] cursor-default" },
  change_request:  { label: "Change Request",  cls: "bg-[#E0F2FE] hover:bg-[#E0F2FE] text-[#075985] cursor-default" },
};

import { AccessDenied } from "@/components/AccessDenied";
import { formatLocal, formatLocalDate } from "@/lib/timezone";

export default function SupportPage() {
  const { user, canSee, hasRole } = useAuth();
  const isInternal = user?.user_type === "internal";
  const navigate = useNavigate();
  const location = useLocation();
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [filterStatus, setFilterStatus] = useState(isInternal ? "active" : "client_view");
  const [form, setForm] = useState({ subject: "", description: "", priority: "medium", category: "general" });
  // Session 68 — files staged in the create dialog (Bug 3). Uploaded
  // sequentially after the ticket POST succeeds so we don't block the
  // primary write path on the (slow, optional) attachment upload.
  const [stagedFiles, setStagedFiles] = useState([]);
  const stagedFileRef = useRef(null);
  const [isStageDragging, setIsStageDragging] = useState(false);
  // Session 70 — guard against the multi-submit bug. A `useRef` flips
  // synchronously on the first click so a rapid double-click can't get
  // past it; the React state mirrors it for the disabled-button UX.
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const [internalUsers, setInternalUsers] = useState([]);
  const [ticketSort, setTicketSort] = useState("newest");
  const [showSlaConfig, setShowSlaConfig] = useState(false);
  const [slaConfig, setSlaConfig] = useState([]);
  const [slaEdits, setSlaEdits] = useState({});
  const [slaSaving, setSlaSaving] = useState(false);
  const isAdmin = hasRole("platform_admin") || hasRole("delivery_admin");

  const loadSlaConfig = async () => {
    try {
      const res = await api.get("/support/sla-config");
      setSlaConfig(res.data);
      const edits = {};
      res.data.forEach(r => { edits[r.priority] = { first_response_hours: r.first_response_hours, resolution_hours: r.resolution_hours }; });
      setSlaEdits(edits);
    } catch {}
  };

  const saveSlaConfig = async () => {
    setSlaSaving(true);
    try {
      const updates = Object.entries(slaEdits).map(([priority, vals]) => ({ priority, ...vals }));
      await api.put("/support/sla-config", updates);
      toast.success("SLA configuration saved");
      loadSlaConfig();
    } catch { toast.error("Failed to save SLA config"); }
    setSlaSaving(false);
  };

  useEffect(() => { loadTickets(); if (isInternal) loadStats(); }, []);

  const loadTickets = async () => {
    try {
      let url = "/support/tickets";
      if (filterStatus === "client_view") {
        // Client sees: open + in_progress + resolved (not closed/cancelled)
        url += "?status=active";
      } else {
        // Always send the filter explicitly — including "all". If we
        // omit the param the backend defaults to `active`, so clicking
        // the "All" tab was silently filtering out resolved/closed/
        // cancelled tickets and rendering an empty table. (Bug fix
        // 4 May 2026.)
        url += `?status=${filterStatus}`;
      }
      const res = await api.get(url);
      let data = res.data;
      // For client_view, also include resolved tickets
      if (filterStatus === "client_view") {
        const resolvedRes = await api.get("/support/tickets?status=resolved");
        data = [...data, ...resolvedRes.data];
      }
      setTickets(data);
    } catch { } finally { setLoading(false); }
  };

  const loadStats = async () => {
    try { const res = await api.get("/support/stats"); setStats(res.data); } catch { }
  };

  useEffect(() => { loadTickets(); }, [filterStatus]);

  // Session 68 — deep-link from notifications + email CTAs. URL shape
  // is `/support?open=SUP-XXXX`. We resolve to the live ticket id once
  // the listing has loaded, then strip the query param so a refresh
  // doesn't re-open. Falls back gracefully if the ticket has been
  // deleted or the user lacks access.
  //
  // Placed BEFORE the AccessDenied early-return so React's
  // rules-of-hooks invariant (same hook order on every render)
  // holds — otherwise the hook becomes conditional and breaks the
  // build with `react-hooks/rules-of-hooks`.
  useEffect(() => {
    if (selectedTicket) return;
    if (loading) return;
    const params = new URLSearchParams(location.search);
    const openNum = params.get("open");
    if (!openNum) return;
    const match = tickets.find(t => t.ticket_number === openNum);
    if (match) {
      openTicket(match.id);
      navigate("/support", { replace: true });
    } else {
      // Ticket not in the current filtered list → try fetching by
      // number directly so the user lands somewhere useful. The
      // backend GET endpoint accepts either the UUID id or the
      // ticket_number, so this single round-trip handles both
      // shapes without an extra resolver.
      api.get(`/support/tickets/${encodeURIComponent(openNum)}`)
        .then(r => { setSelectedTicket(r.data); navigate("/support", { replace: true }); })
        .catch(() => {
          toast.error(`Ticket ${openNum} not found or no longer accessible`);
          navigate("/support", { replace: true });
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickets, loading, location.search, selectedTicket]);

  if (isInternal && !canSee("support")) return <AccessDenied module="Support" />;

  const handleCreate = async () => {
    if (!form.subject.trim()) return toast.error("Subject is required");
    // Session 70 — atomic single-submit guard. The previous version had
    // no guard, so a slow attachment upload (>1s) plus an impatient user
    // double-clicking the button created N parallel POSTs and N tickets.
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      const res = await api.post("/support/tickets", form);
      const newTicket = res.data;

      // Session 68 — Bug 3: upload staged attachments after the
      // ticket exists. We don't fail the whole create if a file
      // upload errors — the ticket is already saved so it's better
      // to surface a partial-success toast than block the user.
      let uploadFails = 0;
      for (const f of stagedFiles) {
        const fd = new FormData();
        fd.append("file", f);
        try {
          await api.post(`/support/tickets/${newTicket.id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        } catch { uploadFails += 1; }
      }
      if (uploadFails > 0) {
        toast.warning(`Ticket ${newTicket.ticket_number} created, but ${uploadFails} attachment(s) failed to upload.`);
      } else if (stagedFiles.length > 0) {
        toast.success(`Ticket ${newTicket.ticket_number} created with ${stagedFiles.length} attachment(s).`);
      } else {
        toast.success(`Ticket ${newTicket.ticket_number} created`);
      }

      setShowCreate(false);
      setForm({ subject: "", description: "", priority: "medium", category: "general" });
      setStagedFiles([]);
      loadTickets();
      if (isInternal) loadStats();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to create ticket"); }
    finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  // Session 68 — file staging helpers for the create dialog.
  const onStageFiles = (e) => {
    // Accept either a real change event or a FileList (so the drag-
    // and-drop wrapper below can call us with the dropped files).
    const incoming = Array.from(e?.target?.files || e || []);
    if (incoming.length === 0) return;
    // Match the existing reply-box ceiling: 5 files, 10 MB each.
    const TEN_MB = 10 * 1024 * 1024;
    const filtered = incoming.filter(f => {
      if (f.size > TEN_MB) {
        toast.error(`"${f.name}" exceeds the 10 MB limit`);
        return false;
      }
      return true;
    });
    setStagedFiles(prev => {
      const merged = [...prev, ...filtered].slice(0, 5);
      if (prev.length + filtered.length > 5) {
        toast.error("Up to 5 attachments per ticket; extras dropped.");
      }
      return merged;
    });
    if (stagedFileRef.current && e?.target?.files) stagedFileRef.current.value = "";
  };
  const removeStaged = (idx) => setStagedFiles(prev => prev.filter((_, i) => i !== idx));
  // Session 69 — drag-and-drop dropzone handlers for the new-ticket
  // dialog. Visual feedback (dashed border) on dragover; clears on
  // dragleave/drop. Keeps the click-to-pick behaviour intact too.
  const onStageDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setIsStageDragging(true); };
  const onStageDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsStageDragging(false); };
  const onStageDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setIsStageDragging(false);
    const dropped = e.dataTransfer?.files;
    if (dropped && dropped.length > 0) onStageFiles(Array.from(dropped));
  };

  const handleCloseTicket = async (ticketId) => {
    try {
      await api.put(`/support/tickets/${ticketId}/close`);
      toast.success("Ticket closed");
      loadTickets();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to close ticket"); }
  };

  const openTicket = async (ticketId) => {
    try {
      const res = await api.get(`/support/tickets/${ticketId}`);
      setSelectedTicket(res.data);
      if (isInternal && !internalUsers.length) {
        const uRes = await api.get("/users?user_type=internal");
        setInternalUsers(uRes.data);
      }
    } catch { toast.error("Failed to load ticket"); }
  };

  if (selectedTicket) {
    return <TicketDetail ticket={selectedTicket} user={user} isInternal={isInternal} internalUsers={internalUsers}
      onBack={() => { setSelectedTicket(null); loadTickets(); if (isInternal) loadStats(); }}
      onRefresh={() => openTicket(selectedTicket.id)} />;
  }

  return (
    <div className="page-enter p-6 sm:p-8 space-y-6" data-testid="support-page">
      <div className="flex items-center justify-start flex-wrap gap-6">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tighter text-[#0A0A0A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Support</h1>
          <p className="text-sm text-[#525252] mt-1">{isInternal ? "Manage support tickets" : "Submit and track support requests"}</p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <Button variant="outline" onClick={() => { loadSlaConfig(); setShowSlaConfig(true); }} className="rounded-none border-[#E5E5E5] h-10 px-3 text-xs font-bold uppercase tracking-wider" data-testid="sla-config-btn">
              <GearSix size={16} className="mr-1.5" /> SLA Settings
            </Button>
          )}
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogTrigger asChild>
            <Button className="bg-[#0055FF] hover:bg-[#0040CC] text-white font-bold rounded-none px-6 h-10" data-testid="new-ticket-btn">
              <Plus size={16} weight="bold" className="mr-2" />New Ticket
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-none border-[#E5E5E5] max-w-lg">
            <DialogHeader><DialogTitle className="font-bold tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Submit Support Request</DialogTitle></DialogHeader>
            <div className="space-y-4 pt-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">Subject</Label>
                <Input value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} className="rounded-none border-[#E5E5E5] h-10" placeholder="Brief summary of the issue" data-testid="ticket-subject" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">Description</Label>
                <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="rounded-none border-[#E5E5E5] min-h-[120px]" placeholder="Describe the issue in detail..." data-testid="ticket-description" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">Priority</Label>
                  <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })} className="w-full h-10 px-3 text-sm border border-[#E5E5E5]" data-testid="ticket-priority">
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">Category</Label>
                  <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="w-full h-10 px-3 text-sm border border-[#E5E5E5]" data-testid="ticket-category">
                    <option value="general">General</option>
                    <option value="technical">Technical Issue</option>
                    <option value="access">Access / Permissions</option>
                    <option value="billing">Billing</option>
                    <option value="feature_request">Feature Request</option>
                    {/* Session 68 pt4 — Change Request track requested
                        by the team. Used when a client wants to scope
                        a change to existing functionality rather than
                        report a bug or request a brand-new feature. */}
                    <option value="change_request">Change Request</option>
                  </select>
                </div>
              </div>
              {/* Session 68 — Bug 3: stage attachments alongside the
                  initial ticket POST. Mirrors the reply box's 5-file /
                  10-MB ceiling. Session 69 — drag-and-drop wrapper. */}
              <div className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">Attachments (optional)</Label>
                <div
                  onDragOver={onStageDragOver}
                  onDragLeave={onStageDragLeave}
                  onDrop={onStageDrop}
                  onClick={() => stagedFileRef.current?.click()}
                  className={`border-2 border-dashed p-4 text-center cursor-pointer transition-colors ${
                    isStageDragging
                      ? "border-[#0055FF] bg-[#EFF6FF]"
                      : "border-[#E5E5E5] bg-[#FAFAFA] hover:bg-[#F5F5F5]"
                  }`}
                  data-testid="ticket-attachment-dropzone"
                >
                  <p className="text-xs text-[#525252]">
                    {isStageDragging ? "Drop files to attach" : "Drag & drop files here, or click to browse"}
                  </p>
                  <p className="text-[10px] text-[#A3A3A3] mt-1">Up to 5 files · max 10 MB each</p>
                </div>
                <input ref={stagedFileRef} type="file" multiple onChange={onStageFiles}
                       className="hidden"
                       data-testid="ticket-attachment-input" />
                {stagedFiles.length > 0 && (
                  <ul className="space-y-1 mt-2" data-testid="ticket-staged-files">
                    {stagedFiles.map((f, idx) => (
                      <li key={idx} className="flex items-center justify-between gap-2 px-2 py-1 bg-[#F5F5F5] text-xs">
                        <span className="truncate">{f.name} <span className="text-[#A3A3A3]">· {(f.size / 1024).toFixed(0)} KB</span></span>
                        <button type="button" onClick={() => removeStaged(idx)} className="text-[#A3A3A3] hover:text-[#DC2626]" data-testid={`remove-staged-${idx}`} aria-label={`Remove ${f.name}`}>
                          <X size={12} />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <p className="text-[10px] text-[#A3A3A3]">Up to 5 files, 10 MB each. Add screenshots, logs, or any document that helps us reproduce the issue.</p>
              </div>
              <Button onClick={handleCreate} disabled={submitting} className="w-full bg-[#0055FF] hover:bg-[#0040CC] text-white font-bold rounded-none h-10 disabled:opacity-60 disabled:cursor-not-allowed" data-testid="submit-ticket-btn">{submitting ? "Submitting…" : "Submit Ticket"}</Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* SLA Config Dialog */}
      <Dialog open={showSlaConfig} onOpenChange={setShowSlaConfig}>
        <DialogContent className="rounded-none border-[#E5E5E5] max-w-lg" data-testid="sla-config-dialog">
          <DialogHeader>
            <DialogTitle className="font-bold tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>SLA Configuration</DialogTitle>
          </DialogHeader>
          <div className="pt-2">
            <p className="text-xs text-[#A3A3A3] mb-4">Set the maximum response and resolution times per ticket priority level.</p>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#F5F5F5]">
                  <th className="px-3 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-[#525252]">Priority</th>
                  <th className="px-3 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-[#525252]">First Response (hours)</th>
                  <th className="px-3 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-[#525252]">Resolution (hours)</th>
                </tr>
              </thead>
              <tbody>
                {["critical", "high", "medium", "low"].map(p => (
                  <tr key={p} className="border-t border-[#F5F5F5]">
                    <td className="px-3 py-2">
                      <Badge className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-none border-0 ${PRIORITY_STYLES[p]?.cls}`}>{p}</Badge>
                    </td>
                    <td className="px-3 py-2">
                      <Input type="number" min={1} value={slaEdits[p]?.first_response_hours || ""} onChange={e => setSlaEdits(prev => ({...prev, [p]: {...prev[p], first_response_hours: parseInt(e.target.value) || 0}}))} className="rounded-none border-[#E5E5E5] h-8 w-24 text-sm" data-testid={`sla-fr-${p}`} />
                    </td>
                    <td className="px-3 py-2">
                      <Input type="number" min={1} value={slaEdits[p]?.resolution_hours || ""} onChange={e => setSlaEdits(prev => ({...prev, [p]: {...prev[p], resolution_hours: parseInt(e.target.value) || 0}}))} className="rounded-none border-[#E5E5E5] h-8 w-24 text-sm" data-testid={`sla-res-${p}`} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Button onClick={saveSlaConfig} disabled={slaSaving} className="mt-4 w-full bg-[#0055FF] hover:bg-[#0040CC] text-white font-bold rounded-none h-10" data-testid="save-sla-btn">
              {slaSaving ? "Saving..." : "Save SLA Configuration"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Stats (internal only) */}
      {isInternal && stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={<Ticket size={20} />} label="Open Tickets" value={stats.open_tickets} />
          <StatCard icon={<CheckCircle size={20} />} label="Resolved Today" value={stats.resolved_today} />
          <StatCard icon={<Warning size={20} />} label="SLA Breached" value={stats.sla_breached} alert={stats.sla_breached > 0} />
          <StatCard icon={<Clock size={20} />} label="Total Tickets" value={stats.total_tickets} />
        </div>
      )}

      {/* Filter tabs */}
      {isInternal ? (
        <div className="flex gap-3 items-center flex-wrap">
          <div className="flex border border-[#E5E5E5]">
            {[{ v: "active", l: "Active" }, { v: "open", l: "Open" }, { v: "in_progress", l: "In Progress" }, { v: "resolved", l: "Resolved" }, { v: "cancelled", l: "Cancelled" }, { v: "all", l: "All" }].map(f => (
              <button key={f.v} onClick={() => setFilterStatus(f.v)} className={`px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${filterStatus === f.v ? "bg-[#0055FF] text-white" : "text-[#525252] hover:bg-[#F5F5F5]"}`} data-testid={`filter-${f.v}`}>{f.l}</button>
            ))}
          </div>
          {/* Session 68 pt4 — column-header sorting replaces this
              dropdown for the day-to-day case; we keep the dropdown
              as a fallback so muscle memory isn't broken. */}
          <select value={ticketSort} onChange={e => setTicketSort(e.target.value)} className="border border-[#E5E5E5] px-3 py-2 text-xs font-bold uppercase tracking-wider focus:outline-none focus:border-[#0055FF]" data-testid="tickets-sort">
            <option value="newest">Sort: Newest</option>
            <option value="oldest">Sort: Oldest</option>
            <option value="priority">Sort: Priority</option>
            <option value="status">Sort: Status</option>
            <option value="subject">Sort: Subject A-Z</option>
            <option value="category">Sort: Category</option>
            <option value="client">Sort: Client</option>
          </select>
        </div>
      ) : null}

      {/* Ticket Table — Session 68 pt4 sortable column layout.
          Replaces the previous "card with sidecar client block" so:
            • Internals see the client *short name* as column 1 on
              the far left (and the wider tenant name no longer
              eats the right edge of the row).
            • Category gets its own pastel-coded column so a glance
              tells you which queue a ticket belongs to.
            • Each header is clickable — click once to sort asc on
              that column, click again to flip descending. The
              dropdown above remains as a familiar fallback. */}
      <div className="border border-[#E5E5E5] overflow-x-auto" data-testid="tickets-table-wrapper">
        <table className="w-full text-sm" data-testid="tickets-table">
          <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
            <tr>
              {isInternal && (
                <SortableHeader sortKey="client" current={ticketSort} onClick={setTicketSort} testId="th-client" align="left">Client</SortableHeader>
              )}
              <SortableHeader sortKey="newest" current={ticketSort} onClick={setTicketSort} testId="th-ticket" align="left">Ticket</SortableHeader>
              <SortableHeader sortKey="subject" current={ticketSort} onClick={setTicketSort} testId="th-subject" align="left">Subject</SortableHeader>
              <SortableHeader sortKey="category" current={ticketSort} onClick={setTicketSort} testId="th-category" align="left">Category</SortableHeader>
              <SortableHeader sortKey="priority" current={ticketSort} onClick={setTicketSort} testId="th-priority" align="left">Priority</SortableHeader>
              <SortableHeader sortKey="status" current={ticketSort} onClick={setTicketSort} testId="th-status" align="left">Status</SortableHeader>
              {isInternal && (
                <SortableHeader sortKey="submitter" current={ticketSort} onClick={setTicketSort} testId="th-submitter" align="left">Submitter</SortableHeader>
              )}
              <SortableHeader sortKey="newest" current={ticketSort} onClick={setTicketSort} testId="th-created" align="left">Created</SortableHeader>
              {!isInternal && <th className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-[#525252] text-right">Action</th>}
            </tr>
          </thead>
          <tbody>
            {(() => {
              const PRIO_ORDER = { critical: 0, high: 1, medium: 2, low: 3 };
              const CAT_ORDER = ["change_request", "feature_request", "technical", "billing", "access", "general"];
              const sortedTickets = [...tickets].sort((a, b) => {
                if (ticketSort === "oldest") return new Date(a.created_at) - new Date(b.created_at);
                if (ticketSort === "priority") return (PRIO_ORDER[a.priority] ?? 9) - (PRIO_ORDER[b.priority] ?? 9);
                if (ticketSort === "status") return (a.status || "").localeCompare(b.status || "");
                if (ticketSort === "subject") return (a.subject || "").localeCompare(b.subject || "");
                if (ticketSort === "category") {
                  const ai = CAT_ORDER.indexOf(a.category || "general");
                  const bi = CAT_ORDER.indexOf(b.category || "general");
                  return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
                }
                if (ticketSort === "client") {
                  // Far-left short_name — fall back to legal name
                  // when a tenant doesn't have a short_name set yet.
                  const an = (a.tenant_short_name || a.tenant_name || "").toLowerCase();
                  const bn = (b.tenant_short_name || b.tenant_name || "").toLowerCase();
                  return an.localeCompare(bn);
                }
                if (ticketSort === "submitter") return (a.submitted_by_name || "").localeCompare(b.submitted_by_name || "");
                return new Date(b.created_at) - new Date(a.created_at); // newest
              });
              return sortedTickets;
            })().map(ticket => {
              const priority = PRIORITY_STYLES[ticket.priority] || PRIORITY_STYLES.medium;
              const status = STATUS_STYLES[ticket.status] || STATUS_STYLES.open;
              const cat = CATEGORY_STYLES[ticket.category] || CATEGORY_STYLES.general;
              return (
                <tr
                  key={ticket.id}
                  onClick={() => openTicket(ticket.id)}
                  className="border-b border-[#F5F5F5] hover:bg-[#FAFAFA] cursor-pointer transition-colors"
                  data-testid={`ticket-${ticket.id}`}
                >
                  {isInternal && (
                    <td className="px-3 py-2.5 align-top whitespace-nowrap">
                      {/* Client short name — drives the leftmost
                          orientation column. We keep the legal name
                          accessible via title= so an internal user
                          can hover for the full name when needed. */}
                      <span className="text-xs font-bold text-[#0A0A0A]" title={ticket.tenant_name || undefined} data-testid={`ticket-${ticket.id}-client`}>
                        {clientDisplayName({ short_name: ticket.tenant_short_name, name: ticket.tenant_name }) || <span className="text-[#A3A3A3] italic font-normal">No client</span>}
                      </span>
                    </td>
                  )}
                  <td className="px-3 py-2.5 align-top whitespace-nowrap">
                    <span className="text-xs font-bold text-[#0055FF]">{ticket.ticket_number}</span>
                    {ticket.source === "email" && <span className="ml-1.5 text-[9px] font-bold uppercase text-[#A3A3A3]">· Email</span>}
                  </td>
                  <td className="px-3 py-2.5 align-top max-w-md">
                    <span className="text-xs font-bold text-[#0A0A0A] truncate block">{ticket.subject}</span>
                    {ticket.message_count > 0 && (
                      <span className="text-[10px] text-[#A3A3A3]">{ticket.message_count} msg{ticket.message_count === 1 ? "" : "s"}{ticket.attachment_count ? ` · ${ticket.attachment_count} attachment${ticket.attachment_count === 1 ? "" : "s"}` : ""}</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 align-top whitespace-nowrap">
                    <Badge className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-none border-0 ${cat.cls}`} data-testid={`ticket-${ticket.id}-category`}>{cat.label}</Badge>
                  </td>
                  <td className="px-3 py-2.5 align-top whitespace-nowrap">
                    <Badge className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-none border-0 ${priority.cls}`}>{priority.label}</Badge>
                  </td>
                  <td className="px-3 py-2.5 align-top whitespace-nowrap">
                    <Badge className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-none border-0 ${status.cls}`}>{status.label}</Badge>
                  </td>
                  {isInternal && (
                    <td className="px-3 py-2.5 align-top whitespace-nowrap">
                      <span className="text-xs text-[#0A0A0A]">{ticket.submitted_by_name || "—"}</span>
                      {ticket.assigned_to_name && <span className="block text-[10px] text-[#A3A3A3]">→ {ticket.assigned_to_name}</span>}
                    </td>
                  )}
                  <td className="px-3 py-2.5 align-top whitespace-nowrap text-xs text-[#525252]">
                    {formatLocalDate(ticket.created_at)}
                  </td>
                  {!isInternal && (
                    <td className="px-3 py-2.5 align-top text-right whitespace-nowrap">
                      {ticket.status === "resolved" && (
                        <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); handleCloseTicket(ticket.id); }}
                          className="rounded-none h-8 text-xs font-bold border-[#16A34A] text-[#16A34A] hover:bg-[#F0FDF4]"
                          data-testid={`close-ticket-${ticket.id}`}>
                          <CheckCircle size={14} className="mr-1" />Close
                        </Button>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
        {!loading && tickets.length === 0 && (
          <div className="text-center py-16">
            <Ticket size={48} className="mx-auto text-[#E5E5E5] mb-4" />
            <p className="text-[#A3A3A3] font-medium">No tickets found</p>
          </div>
        )}
      </div>
    </div>
  );
}

function TicketDetail({ ticket, user, isInternal, internalUsers, onBack, onRefresh }) {
  const [reply, setReply] = useState("");
  const [isInternalNote, setIsInternalNote] = useState(false);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const fileRef = useRef(null);
  const priority = PRIORITY_STYLES[ticket.priority] || PRIORITY_STYLES.medium;
  const status = STATUS_STYLES[ticket.status] || STATUS_STYLES.open;

  const handleSendReply = async () => {
    if (!reply.trim()) return;
    try {
      setSending(true);
      await api.post(`/support/tickets/${ticket.id}/messages`, { content: reply, is_internal_note: isInternalNote });
      toast.success(isInternalNote ? "Internal note added" : "Reply sent");
      setReply("");
      setIsInternalNote(false);
      onRefresh();
    } catch { toast.error("Failed to send"); }
    finally { setSending(false); }
  };

  const handleUpload = async (e) => {
    // Accept either a real change event or a FileList (drag/drop).
    const file = e?.target?.files?.[0] || (Array.isArray(e) ? e[0] : (e?.[0]));
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      setUploading(true);
      await api.post(`/support/tickets/${ticket.id}/attachments`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("File attached");
      onRefresh();
    } catch { toast.error("Upload failed"); }
    finally {
      setUploading(false);
      if (e?.target?.value !== undefined) e.target.value = "";
    }
  };
  // Session 69 — drag-and-drop on the reply box's attach area.
  const [isReplyDragging, setIsReplyDragging] = useState(false);
  const onReplyDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setIsReplyDragging(true); };
  const onReplyDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsReplyDragging(false); };
  const onReplyDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setIsReplyDragging(false);
    const dropped = e.dataTransfer?.files;
    if (dropped && dropped.length > 0) handleUpload(Array.from(dropped));
  };

  const handleCancel = async () => {
    if (!cancelReason.trim()) return toast.error("Please provide a reason");
    try {
      await api.put(`/support/tickets/${ticket.id}/cancel`, { reason: cancelReason });
      toast.success("Ticket cancelled");
      setShowCancel(false);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to cancel"); }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await api.put(`/support/tickets/${ticket.id}`, { status: newStatus });
      toast.success(`Status: ${newStatus.replace(/_/g, " ")}`);
      onRefresh();
    } catch { toast.error("Failed to update"); }
  };

  const handleAssign = async (userId) => {
    try {
      await api.put(`/support/tickets/${ticket.id}`, { assigned_to: userId });
      toast.success("Assigned");
      onRefresh();
    } catch { toast.error("Failed to assign"); }
  };

  const handlePriorityChange = async (p) => {
    try {
      await api.put(`/support/tickets/${ticket.id}`, { priority: p });
      toast.success(`Priority: ${p}`);
      onRefresh();
    } catch { toast.error("Failed"); }
  };

  // SLA indicators
  const now = new Date();
  const responseDue = ticket.sla_first_response_due ? new Date(ticket.sla_first_response_due) : null;
  const resolutionDue = ticket.sla_resolution_due ? new Date(ticket.sla_resolution_due) : null;
  const responseBreached = responseDue && !ticket.first_response_at && now > responseDue;
  const resolutionBreached = resolutionDue && !ticket.resolved_at && now > resolutionDue;

  return (
    <div className="page-enter p-6 sm:p-8 space-y-6" data-testid="ticket-detail">
      <button onClick={onBack} className="flex items-center gap-2 text-sm text-[#525252] hover:text-[#0055FF]" data-testid="ticket-back">
        <ArrowLeft size={16} /> Back to tickets
      </button>

      {/* Session 68 pt4 — split layout. Left column holds the
          ticket metadata (header, SLA, admin controls, attachments).
          Right column is a sticky, scrollable conversation panel
          modeled on TaskChatPanel — the long thread no longer pushes
          everything else off the page, and the reply box is always
          one click away from anywhere in the metadata. The grid
          collapses to a single column under `lg` so mobile/tablet
          users still see the full ticket on a phone. */}
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_420px] gap-6">
        <div className="space-y-6 min-w-0">

      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="text-lg font-bold text-[#0055FF]">{ticket.ticket_number}</span>
            <Badge className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-none border-0 ${priority.cls}`}>{priority.label}</Badge>
            <Badge className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-none border-0 ${status.cls}`}>{status.label}</Badge>
            <Badge className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-none border-0 ${(CATEGORY_STYLES[ticket.category] || CATEGORY_STYLES.general).cls}`} data-testid="ticket-detail-category">
              {(CATEGORY_STYLES[ticket.category] || CATEGORY_STYLES.general).label}
            </Badge>
            {ticket.source === "email" && <Badge className="bg-[#F5F5F5] hover:bg-[#F5F5F5] text-[#A3A3A3] cursor-default text-[10px] rounded-none border-0">Email</Badge>}
          </div>
          <h1 className="text-2xl font-black tracking-tight text-[#0A0A0A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{ticket.subject}</h1>
          <p className="text-xs text-[#A3A3A3] mt-1">Submitted by {ticket.submitted_by_name || ticket.submitted_by_email} &middot; {formatLocal(ticket.created_at)}</p>
          {ticket.tenant_name && (
            <p className="text-xs text-[#525252] mt-0.5" title={ticket.tenant_name}>
              Client: {clientDisplayName({ short_name: ticket.tenant_short_name, name: ticket.tenant_name })}
            </p>
          )}
        </div>
        {/* Cancel button */}
        {ticket.status !== "cancelled" && ticket.status !== "closed" && (
          <div className="flex gap-2">
            {!isInternal && ticket.status === "resolved" && (
              <Button size="sm" onClick={async () => { try { await api.put(`/support/tickets/${ticket.id}/close`); toast.success("Ticket closed"); onRefresh(); } catch { toast.error("Failed"); } }}
                className="bg-[#16A34A] hover:bg-[#15803D] text-white rounded-none h-8 text-xs font-bold" data-testid="close-resolved-btn">
                <CheckCircle size={14} className="mr-1" />Close Ticket
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => setShowCancel(true)} className="rounded-none h-8 text-xs font-bold border-[#DC2626] text-[#DC2626] hover:bg-[#FEF2F2]" data-testid="cancel-ticket-btn">Cancel Ticket</Button>
          </div>
        )}
      </div>

      {/* Client & Submitter Information */}
      <Card className="border border-[#E5E5E5] rounded-none p-4" data-testid="ticket-client-info">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Submitted By */}
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-[#0055FF] mb-2">Submitted By</p>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-[#F5F5F5] flex items-center justify-center text-sm font-bold text-[#525252] flex-shrink-0">
                {(ticket.submitter_name || ticket.submitted_by_name || "?").charAt(0).toUpperCase()}
              </div>
              <div className="text-xs space-y-0.5">
                <p className="font-bold text-[#0A0A0A] text-sm">{ticket.submitter_name || ticket.submitted_by_name || "Unknown"}</p>
                <p className="text-[#525252]">{ticket.submitter_email || ticket.submitted_by_email}</p>
                {(ticket.submitter_phone) && <p className="text-[#525252]">{ticket.submitter_phone}</p>}
                {ticket.submitter_role && <p className="text-[#A3A3A3] capitalize">{ticket.submitter_role.replace(/_/g, " ")}</p>}
                <p className="text-[#A3A3A3]">Via {ticket.source === "email" ? "Email" : "Portal"} &middot; {formatLocal(ticket.created_at)}</p>
              </div>
            </div>
          </div>
          {/* Client Company */}
          {isInternal && (
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-[#7C3AED] mb-2">Client Company</p>
              {ticket.tenant_name ? (
                <div className="text-xs space-y-0.5">
                  <p className="font-bold text-[#0A0A0A] text-sm">{ticket.tenant_name}</p>
                  {ticket.client_contact_name && <p className="text-[#525252]">PM: {ticket.client_contact_name}</p>}
                  {ticket.client_contact_email && <p className="text-[#525252]">{ticket.client_contact_email}</p>}
                  {ticket.client_contact_phone && <p className="text-[#525252]">{ticket.client_contact_phone}</p>}
                  {ticket.client_address && <p className="text-[#A3A3A3]">{ticket.client_address}</p>}
                  <p className="text-[#A3A3A3]">{[ticket.client_city, ticket.client_state, ticket.client_postal_code, ticket.client_country].filter(Boolean).join(", ")}</p>
                </div>
              ) : (
                <p className="text-xs text-[#A3A3A3] italic">No client linked to this ticket</p>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* Cancellation info */}
      {ticket.status === "cancelled" && (
        <Card className="border border-[#DC2626] bg-[#FEF2F2] rounded-none p-4">
          <p className="text-xs font-bold text-[#DC2626] uppercase mb-1">Ticket Cancelled</p>
          <p className="text-sm text-[#525252]"><strong>Reason:</strong> {ticket.cancellation_reason}</p>
          <p className="text-[10px] text-[#A3A3A3] mt-1">Cancelled by {ticket.cancelled_by_name} on {ticket.cancelled_at ? formatLocal(ticket.cancelled_at) : "N/A"}</p>
        </Card>
      )}

      {/* SLA + Actions (internal) */}
      {isInternal && (
        <div className="flex flex-wrap gap-4 items-start">
          <Card className="border border-[#E5E5E5] rounded-none p-4 flex-1 min-w-[200px]">
            <p className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3] mb-2">SLA Status</p>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-xs">
                <span>First Response</span>
                {ticket.first_response_at ? (
                  <span className="text-[#16A34A] font-bold">Met</span>
                ) : responseBreached ? (
                  <span className="text-[#DC2626] font-bold">BREACHED</span>
                ) : (
                  <span className="text-[#D97706] font-bold">Due {responseDue ? formatLocal(responseDue) : "—"}</span>
                )}
              </div>
              <div className="flex items-center justify-between text-xs">
                <span>Resolution</span>
                {ticket.resolved_at ? (
                  <span className="text-[#16A34A] font-bold">Resolved</span>
                ) : resolutionBreached ? (
                  <span className="text-[#DC2626] font-bold">BREACHED</span>
                ) : (
                  <span className="text-[#D97706] font-bold">Due {resolutionDue ? formatLocal(resolutionDue) : "—"}</span>
                )}
              </div>
            </div>
          </Card>
          <Card className="border border-[#E5E5E5] rounded-none p-4 min-w-[200px]">
            <p className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3] mb-2">Actions</p>
            <div className="flex flex-wrap gap-2">
              <select onChange={e => { if (e.target.value) handleAssign(e.target.value); }} value={ticket.assigned_to || ""} className="h-8 px-2 text-xs border border-[#E5E5E5]" data-testid="assign-select">
                <option value="">Assign to...</option>
                {/* Session 68 — Bug 9a: was filtering on
                    `is_support_team || platform_admin`, which left the
                    dropdown showing only one user when no other staff
                    has the support flag set. Now we show every
                    internal user — the support module access matrix
                    already gates who can SEE this view, so no
                    additional gating is needed here. */}
                {internalUsers.map(u => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </select>
              <select onChange={e => { if (e.target.value) handlePriorityChange(e.target.value); }} value={ticket.priority} className="h-8 px-2 text-xs border border-[#E5E5E5]" data-testid="priority-select">
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
              {/* Session 68 — Bug 9b: explicit hover colours on
                  status-action buttons. Without these, the global
                  shadcn `Button` hover (`hover:bg-primary/90`) takes
                  over and turns the green "Resolve" button blue, the
                  blue "Start" button lighter blue, etc. */}
              {ticket.status === "open" && <Button size="sm" onClick={() => handleStatusChange("in_progress")} className="bg-[#0055FF] hover:bg-[#0040CC] text-white rounded-none h-8 text-xs font-bold" data-testid="start-ticket-btn">Start</Button>}
              {(ticket.status === "open" || ticket.status === "in_progress") && <Button size="sm" onClick={() => handleStatusChange("resolved")} className="bg-[#16A34A] hover:bg-[#15803D] text-white rounded-none h-8 text-xs font-bold" data-testid="resolve-ticket-btn">Resolve</Button>}
              {ticket.status === "resolved" && <Button size="sm" onClick={() => handleStatusChange("closed")} className="bg-[#525252] hover:bg-[#404040] text-white rounded-none h-8 text-xs font-bold" data-testid="close-ticket-btn">Close</Button>}
            </div>
          </Card>
        </div>
      )}

      {/* Messages — moved to the right side panel below */}

      {/* Attachments */}
      {ticket.attachments?.length > 0 && (
        <div>
          <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-2">Attachments</h3>
          <div className="flex flex-wrap gap-2">
            {ticket.attachments.map(att => (
              // Session 68 — Bug 7: was `<a href={att.file_url}>` but
              // file_url was empty in the DB (object_storage.upload
              // returns no `url` field), so the empty href fell
              // through to the SPA catch-all → support listing.
              // Now routed through a dedicated download endpoint that
              // re-resolves to the storage key + streams the file
              // with a Content-Disposition: attachment header.
              <a
                key={att.id}
                href={`${process.env.REACT_APP_BACKEND_URL}/api/support/attachments/${att.id}/download`}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-2 border border-[#E5E5E5] px-3 py-2 text-xs text-[#0055FF] hover:bg-[#F5F5F5]"
                data-testid={`attachment-${att.id}`}
              >
                <Paperclip size={14} />{att.file_name}
              </a>
            ))}
          </div>
        </div>
      )}

        </div>
        {/* End left column */}

        {/* Right column — sticky scrollable conversation panel.
            Mimics TaskChatPanel: a scrollable thread with the reply
            box pinned to the bottom of the panel.
            Height is calc'd to fill the viewport minus the page
            chrome so the thread always has its own scroll context
            independent of the metadata column. */}
        <aside
          className="lg:sticky lg:top-6 lg:self-start lg:h-[calc(100vh-100px)] flex flex-col border border-[#E5E5E5] bg-white"
          data-testid="ticket-conversation-panel"
        >
          <div className="px-4 py-3 border-b border-[#E5E5E5] flex items-center justify-between flex-shrink-0">
            <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252]">Conversation</h3>
            <span className="text-[10px] text-[#A3A3A3]" data-testid="ticket-conversation-count">
              {(ticket.messages || []).length} {(ticket.messages || []).length === 1 ? "message" : "messages"}
            </span>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="ticket-conversation-scroll">
            {(ticket.messages || []).length === 0 ? (
              <p className="text-xs text-[#A3A3A3] italic text-center py-8">No messages yet.</p>
            ) : (
              (ticket.messages || []).map(msg => (
                <MessageBubble
                  key={msg.id}
                  msg={msg}
                  user={user}
                  onRefresh={onRefresh}
                />
              ))
            )}
          </div>

          {/* Reply Box — pinned to the bottom of the panel */}
          {ticket.status !== "closed" && ticket.status !== "cancelled" && (
            <div className="border-t border-[#E5E5E5] p-3 space-y-2 flex-shrink-0 bg-[#FAFAFA]">
              <Textarea
                value={reply}
                onChange={e => setReply(e.target.value)}
                onDragOver={onReplyDragOver}
                onDragLeave={onReplyDragLeave}
                onDrop={onReplyDrop}
                placeholder={isInternalNote ? "Internal note (hidden from client)..." : (isReplyDragging ? "Drop files to attach…" : "Type your reply or drag files in to attach…")}
                className={`rounded-none min-h-[70px] text-sm transition-colors ${
                  isReplyDragging
                    ? "border-[#0055FF] bg-[#EFF6FF] ring-2 ring-[#0055FF]"
                    : (isInternalNote ? "border-[#F59E0B] bg-[#FFFBEB]" : "border-[#E5E5E5] bg-white")
                }`}
                data-testid="reply-input"
              />
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                  <input type="file" ref={fileRef} onChange={handleUpload} className="hidden" />
                  <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} className="rounded-none h-7 text-[10px] border-[#E5E5E5] px-2" data-testid="attach-file-btn">
                    <Paperclip size={12} className="mr-1" />{uploading ? "Uploading..." : "Attach"}
                  </Button>
                  {isInternal && (
                    <label className="flex items-center gap-1 text-[10px] cursor-pointer text-[#525252]">
                      <input type="checkbox" checked={isInternalNote} onChange={e => setIsInternalNote(e.target.checked)} className="w-3 h-3" />
                      <Lock size={10} className="text-[#D97706]" />Internal
                    </label>
                  )}
                </div>
                <Button onClick={handleSendReply} disabled={sending || !reply.trim()} className="bg-[#0055FF] hover:bg-[#0040CC] text-white font-bold rounded-none h-8 px-3 text-[10px]" data-testid="send-reply-btn">
                  <PaperPlaneRight size={12} className="mr-1" />{sending ? "Sending..." : "Send"}
                </Button>
              </div>
            </div>
          )}
        </aside>
      </div>
      {/* End grid */}

      {/* Reply Box was here — now lives inside the right panel */}

      {/* Cancel Dialog */}
      <Dialog open={showCancel} onOpenChange={setShowCancel}>
        <DialogContent className="rounded-none border-[#E5E5E5] max-w-md" data-testid="cancel-dialog">
          <DialogHeader><DialogTitle className="font-bold text-[#DC2626]">Cancel Ticket</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-[#525252]">Please provide a reason for cancelling ticket <strong>{ticket.ticket_number}</strong>. The {isInternal ? "client" : "Eyvo team"} will be notified.</p>
            <Textarea value={cancelReason} onChange={e => setCancelReason(e.target.value)} placeholder="Reason for cancellation..." className="rounded-none border-[#E5E5E5] min-h-[80px]" data-testid="cancel-reason" />
            <div className="flex gap-3">
              <Button onClick={handleCancel} className="flex-1 bg-[#DC2626] hover:bg-[#B91C1C] text-white font-bold rounded-none h-10" data-testid="confirm-cancel">Cancel Ticket</Button>
              <Button variant="outline" onClick={() => setShowCancel(false)} className="flex-1 rounded-none h-10 border-[#E5E5E5]">Keep Open</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ icon, label, value, alert }) {
  return (
    <Card className={`border rounded-none p-4 ${alert ? "border-[#DC2626] bg-[#FEF2F2]" : "border-[#E5E5E5]"}`}>
      <div className="flex items-center gap-2 mb-2 text-[#525252]">
        {icon}
        <span className="text-xs font-bold uppercase tracking-[0.15em]">{label}</span>
      </div>
      <span className={`text-3xl font-black tracking-tighter ${alert ? "text-[#DC2626]" : "text-[#0A0A0A]"}`} style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{value}</span>
    </Card>
  );
}

// Session 68 pt4 — column-header sort affordance for the ticket
// table. Click → set the sort key. The currently-active key shows
// a small up-arrow indicator so the user can tell at a glance
// which column drives the order. Keys map 1:1 to the values used
// by the legacy `Sort:` dropdown above so the two sort affordances
// stay in sync.
function SortableHeader({ sortKey, current, onClick, testId, align = "left", children }) {
  const isActive = current === sortKey;
  const alignCls = align === "right" ? "text-right" : "text-left";
  return (
    <th
      onClick={() => onClick(sortKey)}
      className={`px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-[#525252] ${alignCls} cursor-pointer select-none hover:bg-[#F0F0F0]`}
      data-testid={testId}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        {isActive && <span className="text-[#0055FF]" aria-hidden="true">▾</span>}
      </span>
    </th>
  );
}

// Session 68 — Bug 10: per-message edit/delete affordance.
//
// Authors can edit or delete their own messages within
// `edit_window_min` (default 5) of posting. After that, the message
// is immutable and the actions disappear. A live ticking countdown
// is shown so the author knows how long they have left.
//
// Internal notes (`is_internal_note=1`) inherit the same window —
// support agents can correct typos in their notes too. Deleting a
// message hard-removes the row server-side (Session 70); the
// backend simply omits deleted rows on next fetch so this
// component never has to render a tombstone.
function MessageBubble({ msg, user, onRefresh }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(msg.content);
  const [saving, setSaving] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  // Tick once per second so the countdown decays. Cleanup on unmount.
  useEffect(() => {
    if (!msg.created_at) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [msg.created_at]);

  const isMine = user && msg.author_id === user.id;
  const EDIT_WINDOW_MS = 5 * 60 * 1000;
  let createdMs = NaN;
  if (msg.created_at) {
    // Server emits naive ISO from SQL Server DATETIMEOFFSET when no
    // offset is appended. Fall through to lib/timezone's helper if
    // we ever switch shapes; for now `Date.parse` works on both
    // because mssql's pyodbc bridge keeps the +00:00 suffix.
    const parsed = Date.parse(msg.created_at);
    if (!Number.isNaN(parsed)) createdMs = parsed;
  }
  const elapsedMs = createdMs && !Number.isNaN(createdMs) ? now - createdMs : Number.POSITIVE_INFINITY;
  const remainingMs = Math.max(0, EDIT_WINDOW_MS - elapsedMs);
  const canEdit = isMine && remainingMs > 0;

  const fmtRemaining = () => {
    const s = Math.ceil(remainingMs / 1000);
    if (s >= 60) return `${Math.floor(s / 60)}m ${s % 60}s`;
    return `${s}s`;
  };

  const handleSave = async () => {
    if (!draft.trim()) { toast.error("Message cannot be empty"); return; }
    if (draft === msg.content) { setEditing(false); return; }
    setSaving(true);
    try {
      await api.put(`/support/messages/${msg.id}`, { content: draft });
      toast.success("Message updated");
      setEditing(false);
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to edit message");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this message? This cannot be undone — recipients who already received the email notification will still have a copy.")) return;
    try {
      await api.delete(`/support/messages/${msg.id}`);
      toast.success("Message deleted");
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete message");
    }
  };

  const tone = msg.is_internal_note
    ? "border-[#FEF3C7] bg-[#FFFBEB]"
    : msg.author_type === "internal"
      ? "border-[#E5E5E5] bg-[#FAFAFA]"
      : "border-[#E5E5E5] bg-white";

  return (
    <div className={`border p-4 ${tone}`} data-testid={`message-${msg.id}`}>
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <UserAvatar
          name={msg.author_name}
          avatar={msg.author_avatar}
          size="sm"
          testId={`message-${msg.id}-avatar`}
        />
        <span className="text-xs font-bold text-[#0A0A0A]">{msg.author_name}</span>
        <span className="text-[10px] text-[#A3A3A3]">{formatLocal(msg.created_at)}</span>
        {msg.edited_at && (
          <span className="text-[10px] text-[#A3A3A3] italic" title={`Last edited ${formatLocal(msg.edited_at)}`} data-testid={`message-${msg.id}-edited`}>
            (edited)
          </span>
        )}
        {msg.is_internal_note && <Badge className="bg-[#FEF3C7] text-[#92400E] text-[9px] font-bold uppercase px-1.5 py-0 rounded-none border-0 cursor-default hover:bg-[#FEF3C7]"><Lock size={10} className="mr-0.5 inline" />Internal Note</Badge>}
        {msg.author_type === "internal" && !msg.is_internal_note && <Badge className="bg-[#0055FF]/10 text-[#0055FF] text-[9px] font-bold uppercase px-1.5 py-0 rounded-none border-0 cursor-default hover:bg-[#0055FF]/10">Eyvo</Badge>}
        <span className="flex-1" />
        {canEdit && !editing && (
          <>
            <span className="text-[10px] text-[#A3A3A3]" data-testid={`message-${msg.id}-window`}>
              Edit window: {fmtRemaining()}
            </span>
            <button
              onClick={() => { setDraft(msg.content); setEditing(true); }}
              className="text-[10px] text-[#525252] hover:text-[#0055FF] flex items-center gap-1"
              data-testid={`message-${msg.id}-edit-btn`}
            >
              <PencilSimple size={11} />Edit
            </button>
            <button
              onClick={handleDelete}
              className="text-[10px] text-[#525252] hover:text-[#DC2626] flex items-center gap-1"
              data-testid={`message-${msg.id}-delete-btn`}
            >
              <Trash size={11} />Delete
            </button>
          </>
        )}
      </div>
      {editing ? (
        <div className="space-y-2">
          <Textarea
            value={draft}
            onChange={e => setDraft(e.target.value)}
            className="rounded-none border-[#E5E5E5] min-h-[80px] text-sm"
            data-testid={`message-${msg.id}-edit-textarea`}
          />
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={handleSave}
              disabled={saving}
              className="bg-[#0055FF] hover:bg-[#0040CC] text-white rounded-none h-8 text-xs font-bold"
              data-testid={`message-${msg.id}-save-edit-btn`}
            >
              {saving ? "Saving..." : "Save"}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => { setEditing(false); setDraft(msg.content); }}
              className="rounded-none h-8 text-xs border-[#E5E5E5]"
              data-testid={`message-${msg.id}-cancel-edit-btn`}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <p className="text-sm text-[#333] whitespace-pre-wrap">{msg.content}</p>
      )}
    </div>
  );
}
